import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { EVENT_LABELS, type IntegrationDefinition, type IntegrationEventType } from '@shared/integrations'
import { api, ApiError, type IntegrationConnection } from '../lib/api'
import { ReadOnlyNotice } from './ReadOnlyNotice'

const STATUS_TONE: Record<string, 'good' | 'warn' | 'bad' | undefined> = {
  connected: 'good',
  error: 'bad',
  disabled: undefined,
  delivered: 'good',
  pending: 'warn',
  sending: 'warn',
  failed: 'warn',
  dead: 'bad',
}

/**
 * Connected systems.
 *
 * The screen leads with delivery health rather than with a list of logos, because the failure
 * this feature actually has is silence: a token expires, deliveries start failing, and nobody
 * notices for a week because a CRM does not complain about the contacts it never received.
 */
export function Integrations() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<string | null>(null)

  const data = useQuery({ queryKey: ['integrations'], queryFn: api.integrations, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['integrations'] })

  if (data.isPending) return <div className="cad-card" style={{ height: '240px' }} />
  if (data.isError) {
    const message = data.error instanceof ApiError ? data.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { catalogue, connections, activity, canManage } = data.data!
  const byProvider = new Map(connections.map((c) => [c.provider, c]))
  const failing = connections.filter((c) => c.status === 'error')

  return (
    <>
      {!canManage && <ReadOnlyNotice what="integrations" />}
      <header className="stack gap-1">
        <span className="cad-eyebrow">Connected systems</span>
        <h1 className="cad-title">Integrations</h1>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Conversations here can create contacts and tickets in the tools you already use.
          Deliveries are queued and retried, so a system being down delays them rather than
          losing them.
        </p>
      </header>

      {failing.length > 0 && (
        <div className="cad-card" style={{ borderColor: 'var(--cad-bad)' }}>
          <div className="stack gap-2">
            <h2 className="cad-section-title">Needs attention</h2>
            {failing.map((connection) => (
              <p key={connection.id} className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                <b>{catalogue.find((d) => d.id === connection.provider)?.name}</b> stopped
                accepting deliveries: {connection.lastError}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="cad-grid-cards">
        {catalogue.map((definition) => (
          <Card
            key={definition.id}
            definition={definition}
            connection={byProvider.get(definition.id)}
            canManage={canManage}
            open={editing === definition.id}
            onOpen={() => setEditing(editing === definition.id ? null : definition.id)}
            onChanged={() => {
              setEditing(null)
              void refresh()
            }}
          />
        ))}
      </div>

      <InboundWebhookCard canManage={canManage} />

      {activity.length > 0 && (
        <section className="cad-card">
          <div className="stack gap-3">
            <h2 className="cad-section-title">Recent deliveries</h2>
            <div className="cad-grid-frame">
              <table className="cad-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>To</th>
                    <th>Event</th>
                    <th>Result</th>
                    <th className="num">Tries</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.map((event) => {
                    const connection = connections.find((c) => c.id === event.integrationId)
                    const definition = catalogue.find((d) => d.id === connection?.provider)
                    return (
                      <tr key={event.id}>
                        <td className="cad-muted">{new Date(event.createdAt).toLocaleString()}</td>
                        <td>{definition?.name ?? 'Removed'}</td>
                        <td className="cad-muted">{event.type}</td>
                        <td style={{ maxWidth: '32ch', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <span className="cad-pill" data-tone={STATUS_TONE[event.status]}>
                            {event.status}
                          </span>{' '}
                          <span className="cad-muted" title={event.result ?? event.lastError ?? undefined}>
                            {event.result ?? event.lastError ?? ''}
                          </span>
                        </td>
                        <td className="num">{event.attempts}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <p className="cad-note">
              A delivery that fails is tried again with a longer gap each time. After six
              attempts it stops and waits for you, so a broken token does not turn into
              thousands of pointless requests.
            </p>
          </div>
        </section>
      )}
    </>
  )
}

/**
 * The inverse of everything above: an address and a key an external system uses to call into
 * this workspace, rather than one Wami calls out with. The key is shown once, right after it
 * is minted — the same "cannot be shown again" contract an MCP agent credential already makes,
 * for the same reason: it is 256 random bits, nothing about it needs recovering, only rotating.
 */
function InboundWebhookCard({ canManage }: { canManage: boolean }) {
  const queryClient = useQueryClient()
  const [justRotated, setJustRotated] = useState<{ token: string; displayHint: string } | null>(null)
  const [copied, setCopied] = useState(false)

  const settings = useQuery({ queryKey: ['inbound-webhook'], queryFn: api.inboundWebhook, retry: false })
  const rotate = useMutation({
    mutationFn: api.rotateInboundWebhookKey,
    onSuccess: (result) => {
      setJustRotated({ token: result.token, displayHint: result.displayHint })
      setCopied(false)
      void queryClient.invalidateQueries({ queryKey: ['inbound-webhook'] })
    },
  })

  if (settings.isPending || !settings.data) return null

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        <h2 className="cad-section-title">Inbound webhook</h2>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Lets another system reach into this workspace — a HubSpot workflow sending a WhatsApp
          message, a Zap setting a chat property. Point it at the address below with the key as
          a bearer token; it can send a message, add a label, or set a property, and nothing
          else.
        </p>

        <div className="cad-field">
          <label>Endpoint</label>
          <code className="cad-input" style={{ display: 'block', overflowX: 'auto' }}>{settings.data.url}</code>
        </div>

        {justRotated ? (
          <div className="cad-note" style={{ borderColor: 'var(--cad-accent)' }}>
            <div className="stack gap-2">
              <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
                <b>Copy this key now — it will not be shown again</b>
                <span className="cad-pill" data-tone="warn">Shown once</span>
              </div>
              <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                <code>{justRotated.token}</code>
                <button
                  type="button"
                  className="cad-btn"
                  data-variant="text"
                  onClick={() => {
                    navigator.clipboard?.writeText(justRotated.token)
                    setCopied(true)
                  }}
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="cad-muted">
            {settings.data.displayHint ? `Current key: ${settings.data.displayHint}` : 'No key has been generated yet.'}
          </p>
        )}

        {rotate.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {rotate.error instanceof Error ? rotate.error.message : 'Could not generate a key.'}
          </p>
        )}

        {canManage && (
          <button type="button" className="cad-btn" data-variant="quiet" disabled={rotate.isPending} onClick={() => rotate.mutate()}>
            {rotate.isPending ? 'Generating…' : settings.data.displayHint ? 'Rotate key' : 'Generate key'}
          </button>
        )}
      </div>
    </section>
  )
}

function Card({
  definition,
  connection,
  canManage,
  open,
  onOpen,
  onChanged,
}: {
  definition: IntegrationDefinition
  connection?: IntegrationConnection
  canManage: boolean
  open: boolean
  onOpen: () => void
  onChanged: () => void
}) {
  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['integrations'] })

  const test = useMutation({ mutationFn: () => api.testIntegration(definition.id), onSuccess: refresh })
  const retry = useMutation({ mutationFn: () => api.retryIntegration(definition.id), onSuccess: refresh })
  const disconnect = useMutation({
    mutationFn: () => api.disconnectIntegration(definition.id),
    onSuccess: onChanged,
  })

  const dead = connection?.counts.dead ?? 0

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        <div className="row between gap-2" style={{ alignItems: 'flex-start' }}>
          <div className="stack gap-1">
            <h3 className="cad-section-title">{definition.name}</h3>
            <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
              {definition.summary}
            </p>
          </div>
          {connection && (
            <span className="cad-pill" data-tone={STATUS_TONE[connection.status]}>
              {connection.status}
            </span>
          )}
        </div>

        {connection && (
          <div className="stack gap-1">
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              {connection.lastEventAt
                ? `Last delivery ${new Date(connection.lastEventAt).toLocaleString()}`
                : 'Nothing delivered yet.'}
            </span>
            {dead > 0 && (
              <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                {dead} {dead === 1 ? 'delivery' : 'deliveries'} gave up.
              </span>
            )}
          </div>
        )}

        {test.data && <p className="cad-note">{test.data.detail}</p>}
        {test.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {test.error instanceof Error ? test.error.message : 'The check failed.'}
          </p>
        )}
        {retry.data && <p className="cad-note">{retry.data.requeued} queued to try again.</p>}
        {retry.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {retry.error instanceof Error ? retry.error.message : 'Could not requeue those.'}
          </p>
        )}
        {disconnect.error && (
          <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
            {disconnect.error instanceof Error ? disconnect.error.message : 'Could not disconnect.'}
          </p>
        )}

        {canManage && (
          <div className="row gap-2 wrap">
            <button type="button" className="cad-btn" data-variant={connection ? 'quiet' : undefined} onClick={onOpen}>
              {connection ? (open ? 'Close' : 'Settings') : 'Connect'}
            </button>
            {connection && (
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={test.isPending}
                onClick={() => test.mutate()}
              >
                {test.isPending ? 'Checking…' : 'Test'}
              </button>
            )}
            {dead > 0 && (
              <button
                type="button"
                className="cad-btn"
                data-variant="quiet"
                disabled={retry.isPending}
                onClick={() => retry.mutate()}
              >
                Try the failed ones again
              </button>
            )}
          </div>
        )}

        {open && canManage && (
          <Form
            definition={definition}
            connection={connection}
            onSaved={onChanged}
            onDisconnect={connection ? () => disconnect.mutate() : undefined}
            disconnecting={disconnect.isPending}
          />
        )}
      </div>
    </section>
  )
}

function Form({
  definition,
  connection,
  onSaved,
  onDisconnect,
  disconnecting,
}: {
  definition: IntegrationDefinition
  connection?: IntegrationConnection
  onSaved: () => void
  onDisconnect?: () => void
  disconnecting: boolean
}) {
  const [credentials, setCredentials] = useState<Record<string, string>>({})
  const [options, setOptions] = useState<Record<string, string>>(connection?.options ?? {})
  const [events, setEvents] = useState<string[]>(connection?.events ?? [])

  const save = useMutation({
    mutationFn: () => api.saveIntegration(definition.id, { credentials, options, events }),
    onSuccess: onSaved,
  })

  const toggle = (event: IntegrationEventType) =>
    setEvents((current) =>
      current.includes(event) ? current.filter((e) => e !== event) : [...current, event],
    )

  return (
    <div className="stack gap-3" style={{ borderTop: '1px solid var(--cad-line)', paddingTop: '16px' }}>
      <p className="cad-note">{definition.where}</p>

      {definition.options.map((field) => (
        <div key={field.key} className="cad-field">
          <label htmlFor={`${definition.id}-${field.key}`}>
            {field.label}
            {field.optional && <span className="cad-muted"> — optional</span>}
          </label>
          <input
            id={`${definition.id}-${field.key}`}
            className="cad-input"
            value={options[field.key] ?? ''}
            placeholder={field.hint}
            onChange={(e) => setOptions({ ...options, [field.key]: e.target.value })}
          />
        </div>
      ))}

      {definition.credentials.map((field) => (
        <div key={field.key} className="cad-field">
          <label htmlFor={`${definition.id}-${field.key}`}>
            {field.label}
            {field.optional && <span className="cad-muted"> — optional</span>}
          </label>
          <input
            id={`${definition.id}-${field.key}`}
            className="cad-input"
            type={field.secret ? 'password' : 'text'}
            autoComplete="off"
            value={credentials[field.key] ?? ''}
            // A stored secret is never sent back to the browser, so the field starts empty
            // and an empty field on save means "keep what you have".
            placeholder={connection && field.secret ? 'Saved — leave blank to keep' : field.hint}
            onChange={(e) => setCredentials({ ...credentials, [field.key]: e.target.value })}
          />
          {field.hint && !(connection && field.secret) && (
            <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              {field.hint}
            </span>
          )}
        </div>
      ))}

      <div className="stack gap-2">
        <span className="cad-eyebrow">Send when</span>
        {definition.events.map((event) => (
          <label key={event} className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={events.length === 0 || events.includes(event)}
              onChange={() => toggle(event)}
            />
            <span>{EVENT_LABELS[event]}</span>
          </label>
        ))}
        <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
          Leaving all of them ticked keeps everything this integration supports.
        </span>
      </div>

      {save.error && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {save.error instanceof Error ? save.error.message : 'That did not work.'}
        </p>
      )}

      <div className="row gap-2 wrap">
        <button type="button" className="cad-btn" disabled={save.isPending} onClick={() => save.mutate()}>
          {/* The credentials are proved against the live API before anything is stored. */}
          {save.isPending ? 'Checking…' : connection ? 'Save and re-check' : 'Connect'}
        </button>
        {onDisconnect && (
          <button
            type="button"
            className="cad-btn"
            data-variant="text"
            disabled={disconnecting}
            onClick={() => {
              if (confirm(`Disconnect ${definition.name}?\n\nStored credentials are deleted and every event routed here stops firing. You will need to re-enter the credentials to reconnect.`)) onDisconnect?.()
            }}
          >
            Disconnect
          </button>
        )}
      </div>
    </div>
  )
}
